import React, { useCallback, useRef, useEffect } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import CommentAddIcon from '@atlaskit/icon/core/comment-add';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss, Box, Pressable, Flex, Inline } from '@atlaskit/primitives';

import { fg } from '@confluence/feature-gating';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks';
import { usePageContentId } from '@confluence/page-context';

const addCommentButtonStyle = xcss({
	display: 'flex',
	minHeight: '32px',
	padding: 'space.0',
	paddingLeft: 'space.150',
	paddingRight: 'space.150',
	justifyContent: 'center',
	alignItems: 'center',
	borderRadius: 'radius.small',
	font: token('font.body'),
});

const enabledStyle = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	':hover': {
		cursor: 'pointer',
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
	color: 'color.text.subtle',
});

const disabledStyle = xcss({
	backgroundColor: 'color.background.disabled',
	cursor: 'not-allowed',
	color: 'color.text.disabled',
});

const i18n = defineMessages({
	addCommentPromptText: {
		id: 'object-comments.add-comment-prompt.text',
		defaultMessage: 'Add a comment',
		description: 'Label for the add comment prompt',
	},
	addCommentPromptAriaLabel: {
		id: 'object-comments.add-comment-prompt.aria-label',
		defaultMessage: 'Add a general comment',
		description: 'The aria label for the add general comment entry prompt field',
	},
});

export const ObjectAddCommentPrompt = ({
	onClick,
	autofocusRef,
	onFocus,
	onBlur,
}: {
	onClick: () => void;
	autofocusRef: { current: boolean | null };
	onFocus: () => void; // For both clicking into the box and focusing the element via the tab key
	onBlur: () => void; // For when the box - or its children - loses focus
}) => {
	const { formatMessage } = useIntl();
	const [contentId] = usePageContentId();

	const { canAddComments } = useAddCommentPermissionCheck(contentId || '');

	const buttonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (autofocusRef.current) {
			buttonRef.current?.focus();
			autofocusRef.current = false;
		}
	}, [autofocusRef]);

	// handleFocus() will open the object comment entry when either the user clicks in the element or tabs to it.
	// Note that the focus event will occur before the click event, so we need to stop the propagation of the
	// focus event to prevent it from firing a click event as well.  Therefore if we do see a click event, we know
	// that the user has clicked in the element when we already had focus - that is, the general comment entry was
	// cancelled and put us back here.
	const handleFocus = useCallback(
		(event: React.FocusEvent) => {
			// Prevent the focus event from further processing and firing a click event
			event.stopPropagation();
			onFocus();
		},
		[onFocus],
	);

	const allowAddComment = fg('confluence-fe-comment-add-footer-perms') ? canAddComments : true;
	return (
		<Box>
			<Pressable
				xcss={[addCommentButtonStyle, !allowAddComment ? disabledStyle : enabledStyle]}
				isDisabled={!allowAddComment}
				onClick={onClick}
				testId="add-comment-button"
				ref={buttonRef}
				// TODO - COMMENTS-4934: Clean up confluence_frontend_comments_a11y_kb FG
				{...(fg('confluence_frontend_comments_a11y_kb')
					? {
							'aria-label': formatMessage(i18n.addCommentPromptAriaLabel),
							onFocus: handleFocus,
							onBlur,
						}
					: {})}
			>
				<Flex alignItems="center" gap="space.100">
					<CommentAddIcon label={formatMessage(i18n.addCommentPromptText)} />
					<Inline>{formatMessage(i18n.addCommentPromptText)}</Inline>
				</Flex>
			</Pressable>
		</Box>
	);
};
